import { angularError, timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
/**
 * Compose un trajet aléatoire à `nTurns` virages (donc `nTurns + 1` segments).
 *
 * Les bearings tournent de ±45° à ±90° entre segments (sens choisi au hasard).
 * Longueurs in [50, 100].
 *
 * `trueBackBearing` = bearing depuis la position finale vers (0,0), exprimé
 * dans le repère compas (0 = N, 90 = E, 180 = S, 270 = O).
 */
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    const safeTurns = Math.max(1, Math.min(6, Math.round(value)));
    const nSegments = safeTurns + 1;
    const path = [];
    // Bearing initial aléatoire dans [0, 360).
    let prevBearing = Math.random() * 360;
    for (let i = 0; i < nSegments; i += 1) {
        const length = 50 + Math.random() * 50;
        if (i === 0) {
            path.push({ bearing: ((prevBearing % 360) + 360) % 360, length });
            continue;
        }
        // Tourne de ±45° à ±90° par rapport au bearing précédent.
        const turnMag = 45 + Math.random() * 45;
        const sign = Math.random() < 0.5 ? -1 : 1;
        const bearing = ((prevBearing + sign * turnMag) % 360 + 360) % 360;
        path.push({ bearing, length });
        prevBearing = bearing;
    }
    // Position finale (cumul vecteurs). Compas : 0=N (-y), 90=E (+x).
    let sumX = 0;
    let sumY = 0;
    for (const seg of path) {
        const rad = (seg.bearing * Math.PI) / 180;
        sumX += Math.sin(rad) * seg.length;
        sumY += -Math.cos(rad) * seg.length;
    }
    // Bearing depuis (sumX, sumY) vers (0, 0) : direction (-sumX, -sumY).
    // Reconvertir vecteur (dx, dy) en bearing compas : atan2(dx, -dy).
    const dx = -sumX;
    const dy = -sumY;
    let trueBackBearing = (Math.atan2(dx, -dy) * 180) / Math.PI;
    if (trueBackBearing < 0)
        trueBackBearing += 360;
    trueBackBearing = trueBackBearing % 360;
    return {
        trialIdx,
        nTurns: safeTurns,
        path,
        trueBackBearing,
        pathShowMs: variant.pathShowMs,
    };
}
export function createTrueNorthAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        walkerSegmentIdx: 0,
        lastResponse: null,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    return {
        mount() {
            // Idempotent — rendu déclaratif côté React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    walkerSegmentIdx: 0,
                    lastResponse: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'path_show', walkerSegmentIdx: 0 });
                    // Anime chaque segment séquentiellement.
                    for (let i = 1; i < stim.path.length; i += 1) {
                        const idx = i;
                        timers.push(setTimeout(() => {
                            update({ walkerSegmentIdx: idx });
                        }, timing.segmentAnimMs * idx));
                    }
                    // Bascule sur awaiting_pointer après tout le path animé.
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_pointer' });
                        trialStart = Date.now();
                    }, timing.segmentAnimMs * stim.path.length));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePoint(bearing) {
            if (state.subPhase !== 'awaiting_pointer')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const errorDeg = angularError(bearing, stim.trueBackBearing);
            const correct = errorDeg < state.variant.acceptanceDeg;
            const rt = Date.now() - trialStart;
            const resp = {
                pickedBearing: ((bearing % 360) + 360) % 360,
                trueBackBearing: stim.trueBackBearing,
                errorDeg,
                correct,
                reactionTimeMs: rt,
            };
            update({ lastResponse: resp });
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map